import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";

interface Lead {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  tipo_unidad: string | null;
  m2: string | null;
  fecha_inicio: string | null;
  mensaje: string | null;
  estado: "nuevo" | "contactado" | "cerrado";
  created_at: string;
}

const ESTADO_LABELS: Record<Lead["estado"], string> = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  cerrado: "Cerrado",
};

const ESTADO_COLORS: Record<Lead["estado"], string> = {
  nuevo: "bg-accent-100 text-accent-800",
  contactado: "bg-secondary-100 text-secondary-900",
  cerrado: "bg-background-200 text-foreground-700",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"todos" | Lead["estado"]>("todos");

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/leads`);
    setLeads(await res.json());
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function updateEstado(id: number, estado: Lead["estado"]) {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, estado } : l)));
    await fetch(`${API_BASE}/leads/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ estado }),
    });
  }

  const filtered = filter === "todos" ? leads : leads.filter((l) => l.estado === filter);

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Contactos</h1>
      <p className="mt-1 text-sm text-foreground-500">Mensajes recibidos desde el formulario de contacto.</p>

      <div className="mt-4 flex gap-2">
        {(["todos", "nuevo", "contactado", "cerrado"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold cursor-pointer ${
              filter === f ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
            }`}
          >
            {f === "todos" ? "Todos" : ESTADO_LABELS[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-foreground-500">Cargando...</p>
      ) : filtered.length === 0 ? (
        <p className="mt-6 text-sm text-foreground-500">No hay contactos en este filtro.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((l) => (
            <div key={l.id} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{l.nombre}</p>
                  <p className="text-xs text-foreground-500">
                    {l.email} · {l.telefono}
                  </p>
                </div>
                <select
                  value={l.estado}
                  onChange={(e) => updateEstado(l.id, e.target.value as Lead["estado"])}
                  className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 cursor-pointer ${ESTADO_COLORS[l.estado]}`}
                >
                  {(["nuevo", "contactado", "cerrado"] as const).map((e) => (
                    <option key={e} value={e}>
                      {ESTADO_LABELS[e]}
                    </option>
                  ))}
                </select>
              </div>
              {(l.tipo_unidad || l.m2) && (
                <p className="mt-2 text-xs text-foreground-500">
                  {l.tipo_unidad} {l.m2 && `· ${l.m2.includes("m²") ? l.m2 : `${l.m2} m²`}`}{" "}
                  {l.fecha_inicio && `· desde ${l.fecha_inicio}`}
                </p>
              )}
              {l.mensaje && <p className="mt-2 text-sm text-foreground-700">{l.mensaje}</p>}
              <p className="mt-2 text-xs text-foreground-400">{new Date(l.created_at).toLocaleString("es-CL")}</p>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
